<template>
  <div class="transparent-border fipa-card">
    <v-card class="fipa-card--content" style="width: 100%">
      <v-card color="primary" class="fipa-card--header" flat>
        <h3 class="fipa-card--header--title">
          <slot name="title" />
        </h3>
        <h4 class="fipa-card--header--subtitle">
          <slot name="subtitle" />
        </h4>
      </v-card>
      <article>
        <template v-if="chunks.length <= 2">
          <v-row dense>
            <div v-for="discipline in filledDisciplines" :key="discipline.id">
              <router-link
                v-if="!discipline.isDummy"
                class="discipline-badge"
                :class="discipline.name"
                :to="to({ disciplinesSelected: [discipline.id] })"
              >
                <div v-if="returnImage(discipline)">
                  <img :src="returnImage(discipline)" />
                </div>
                <div v-else-if="$federation.sprites">
                  <svg :class="discipline.name + '-svg'">
                    <use :href="`${$federation.sprites}#${discipline.name}`" />
                  </svg>
                </div>
                <div>{{ discipline.i18nShort }}</div>
              </router-link>
            </div>
          </v-row>
        </template>
        <template v-else>
          <v-row dense>
            <div v-for="discipline in filledDisciplines" :key="discipline.id">
              <!-- Create dummy discipline box-->
              <div v-if="discipline.isDummy" class="discipline-badge" />
              <router-link
                v-else
                class="discipline-badge"
                :class="discipline.name"
                :to="to({ disciplinesSelected: [discipline.id] })"
              >
                <div v-if="returnImage(discipline)">
                  <img :src="returnImage(discipline)" />
                </div>
                <div v-else-if="$federation.sprites">
                  <svg :class="discipline.name + '-svg'">
                    <use :href="`${$federation.sprites}#${discipline.name}`" />
                  </svg>
                </div>
                <div>{{ discipline.i18nShort }}</div>
              </router-link>
            </div>

            <div
              v-if="chunkLast.length <= 2"
              class="to-calendar-parent to-calendar-parent-absolute"
            >
              <v-btn text color="primary" class="to-calendar mx-1" :to="to({})">
                {{ $t("calendar:disciplineBadges.AllDisciplines.link") }}
                <v-icon right small> $iconCircleArrowRight </v-icon>
              </v-btn>
            </div>
          </v-row>
        </template>
      </article>
      <div v-if="chunkLast.length > 2" style="padding: 0 20px">
        <hr class="primary mt-2" />
        <v-card-actions style="justify-content: flex-end" class="pl-6">
          <v-btn text color="primary" :to="to({})">
            {{ $t("calendar:disciplineBadges.AllDisciplines.link") }}
            <v-icon right small> $iconCircleArrowRight </v-icon>
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </div>
</template>
<script>
import { defineComponent, computed, getCurrentInstance } from "vue";
import { chunk, clone } from "lodash-es";

import BelgiumMounted from "./belgium-mounted.png";
import BelgiumHorseball from "./belgium-horseball.png";
import BelgiumTREC from "./belgium-trec.png";

export default defineComponent({
  name: "AppDisicplinesCard",
  props: {
    disciplines: {
      type: Array,
      required: true,
      default: () => []
    },
    params: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const instance = getCurrentInstance();

    const chunks = computed(() => {
      return chunk(props.disciplines, 4);
    });

    const chunkLast = computed(() => {
      if (chunks.value[chunks.value.length - 1])
        return chunks.value[chunks.value.length - 1];
      return [];
    });

    const filledDisciplines = computed(() => {
      const disciplines = clone(props.disciplines);
      // Because of justify-content: center; push dummy objects so it a row with less than 4 disciplines still align with above boxes
      chunks.value.forEach(chunk => {
        if (chunk.length == 4) return;
        let length = chunk.length;
        do {
          disciplines.push({ isDummy: true });
          length++;
        } while (length < 4);
      });
      return disciplines;
    });

    function returnImage(discipline) {
      switch (instance.proxy.$federation.countryCode) {
        case "BE":
        case "NL":
          switch (discipline.name) {
            case "mountedgames": {
              return BelgiumMounted;
            }
            case "horseball": {
              return BelgiumHorseball;
            }
            case "trec": {
              return BelgiumTREC;
            }
          }
      }

      return null;
    }

    function to(params) {
      return {
        name: "Calendar",
        query: Object.assign({}, props.params, params),
        params: { filterParams: Object.assign({}, props.params, params) }
      };
    }

    return {
      chunks,
      filledDisciplines,
      to,
      returnImage,
      chunkLast
    };
  }
});
</script>
<style lang="scss" scoped>
.fipa-card article {
  & > div.row {
    justify-content: space-between;
  }
}

$margin: 0.5em;
.discipline-badge {
  width: 66px;
  height: 66px;
  border-radius: 2px;
  font-size: 11px;
  color: white !important;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: $margin;
  line-height: 1;

  @media (max-width: 576px) {
    margin: 0.25em;
  }
  div {
    padding: 2px;
    max-width: 66px;
    max-height: 50%;
    text-align: center;
    img {
      width: 27px;
      height: 26px;
    }
    svg {
      fill: white;
      width: 27px;
      height: 26px;
      &.vaulting-svg,
      &.driving-svg,
      &.paradriving-svg {
        width: 42px;
      }
      &.endurance-svg {
        width: 32px;
      }
    }
  }
}

.to-calendar-parent {
  display: flex;
  flex-shrink: 1;
  justify-content: flex-end;

  &-absolute {
    position: absolute;
    right: 0;
    bottom: 0;
    transform: translate(-20px, -10px);
  }
}

.to-calendar {
  margin: $margin;
  @media (max-width: 576px) {
    margin: 0.25em;
  }
  text-align: right;
  align-self: flex-end;
  a {
    position: relative;
    top: 8px;
    left: 15px;
  }
}
</style>
